import {
  ARROW_HEADS,
  DASH_STYLES,
  DEFAULT_PALETTE,
  DRAWING_KINDS,
  clampStrokeWidth,
  type ArrowHead,
  type DashStyle,
  type DrawingKind,
} from '@4cm/core/tactics/drawings';
import { HudChip } from './HudChip';
import './DrawingToolbar.css';

/**
 * §7 Drawing-tools palette — the HUD row that selects which drawing
 * primitive the coach is about to draw plus its style (colour / stroke
 * width / dash pattern / optional arrow head). The toolbar itself does
 * NOT capture pointer events; the on-pitch capture layer (mounted by
 * the boards) reads these props as the active style bag.
 *
 * Layout follows the rest of the HUD: a "DRAW" label, then chip rows
 * — kinds → palette swatches → dash → arrow-head (visible only when an
 * arrow kind is active) → stroke-width slider. Toggling the active kind
 * chip a second time clears the selection (passes `null`), so coaches
 * can flick back to the regular Move tool without leaving the toolbar.
 */

const KIND_LABELS: Record<DrawingKind, string> = {
  line: 'Line',
  arrow: 'Arrow',
  'curved-arrow': 'Curve',
  rect: 'Rect',
  ellipse: 'Ellipse',
  text: 'Text',
  freehand: 'Freehand',
  measure: 'Measure',
  zone: 'Zone',
};

// Most chips use the generic `Tool: <kind>` aria-label scheme; the
// distance-measure + spacing-zone tools get discoverable descriptions
// so they read naturally to a coach hunting for "ruler" / "spacing".
const KIND_ARIA_LABELS: Partial<Record<DrawingKind, string>> = {
  measure: 'Distance measure tool',
  zone: 'Spacing zone tool — tap corners, double-tap to close',
};

const DASH_LABELS: Record<DashStyle, string> = {
  solid: 'Solid',
  dashed: 'Dashed',
  dotted: 'Dotted',
};

const ARROW_HEAD_LABELS: Record<ArrowHead, string> = {
  none: 'No head',
  standard: 'Standard',
  open: 'Open',
  curved: 'Curved',
};

// Accessible swatch names so screen readers don't read raw hex codes.
// Order matches DEFAULT_PALETTE (amber-first per broadcast convention).
const PALETTE_NAMES = [
  'amber',
  'green',
  'white',
  'red',
  'blue',
  'magenta',
  'cyan',
  'grey',
] as const;

const STROKE_MIN = 0.1;
const STROKE_MAX = 2.0;
const STROKE_STEP = 0.05;

export interface DrawingToolbarProps {
  activeKind: DrawingKind | null;
  onSelectKind(kind: DrawingKind | null): void;
  stroke: string;
  onStrokeChange(hex: string): void;
  strokeWidth: number;
  onStrokeWidthChange(metres: number): void;
  dash: DashStyle;
  onDashChange(dash: DashStyle): void;
  arrowHead?: ArrowHead;
  onArrowHeadChange?(head: ArrowHead): void;
}

export function DrawingToolbar({
  activeKind,
  onSelectKind,
  stroke,
  onStrokeChange,
  strokeWidth,
  onStrokeWidthChange,
  dash,
  onDashChange,
  arrowHead,
  onArrowHeadChange,
}: DrawingToolbarProps) {
  const showArrowHeadRow =
    (activeKind === 'arrow' || activeKind === 'curved-arrow') &&
    typeof onArrowHeadChange === 'function';

  // Compare hex case-insensitively so a stored '#FFFFFF' still matches
  // the palette's '#ffffff'.
  const strokeLower = stroke.toLowerCase();
  // Clamp slider value defensively — callers may persist out-of-range
  // strokes that we want to re-clamp on first render.
  const sliderValue = clampStrokeWidth(strokeWidth);

  return (
    <div className="thud__row thud__row--draw" data-testid="drawing-toolbar">
      <span className="thud__label">DRAW</span>

      {/* --- kind chips --------------------------------------------------- */}
      <div className="thud__row__main thud__draw-group" role="group" aria-label="Drawing tool">
        {DRAWING_KINDS.map((k) => (
          <HudChip
            key={k}
            active={activeKind === k}
            ariaLabel={KIND_ARIA_LABELS[k] ?? `Tool: ${k}`}
            onClick={() => onSelectKind(activeKind === k ? null : k)}
          >
            {KIND_LABELS[k]}
          </HudChip>
        ))}
      </div>

      {/* --- colour swatches --------------------------------------------- */}
      <div
        className="thud__draw-group thud__draw-palette"
        role="group"
        aria-label="Stroke colour"
      >
        {DEFAULT_PALETTE.map((hex, i) => {
          const name = PALETTE_NAMES[i] ?? hex;
          const isActive = strokeLower === hex.toLowerCase();
          return (
            <button
              key={hex}
              type="button"
              className="thud__swatch"
              data-active={isActive || undefined}
              aria-pressed={isActive}
              aria-label={`Colour: ${name}`}
              style={{ background: hex }}
              onClick={() => onStrokeChange(hex)}
            />
          );
        })}
      </div>

      {/* --- dash row ----------------------------------------------------- */}
      <div className="thud__draw-group" role="group" aria-label="Dash style">
        {DASH_STYLES.map((d) => (
          <HudChip
            key={d}
            active={dash === d}
            ariaLabel={`Dash: ${d}`}
            onClick={() => onDashChange(d)}
          >
            {DASH_LABELS[d]}
          </HudChip>
        ))}
      </div>

      {/* --- arrow-head row (conditional) -------------------------------- */}
      {showArrowHeadRow && (
        <div
          className="thud__draw-group"
          role="group"
          aria-label="Arrow head"
          data-testid="drawing-toolbar-arrow-heads"
        >
          {ARROW_HEADS.map((h) => (
            <HudChip
              key={h}
              active={(arrowHead ?? 'standard') === h}
              ariaLabel={`Arrow head: ${h}`}
              onClick={() => onArrowHeadChange?.(h)}
            >
              {ARROW_HEAD_LABELS[h]}
            </HudChip>
          ))}
        </div>
      )}

      {/* --- stroke-width slider ----------------------------------------- */}
      <label className="thud__draw-slider">
        <span className="thud-sr-only">Stroke width</span>
        <input
          type="range"
          min={STROKE_MIN}
          max={STROKE_MAX}
          step={STROKE_STEP}
          value={sliderValue}
          aria-label="Stroke width"
          aria-valuemin={STROKE_MIN}
          aria-valuemax={STROKE_MAX}
          aria-valuenow={sliderValue}
          onChange={(e) =>
            onStrokeWidthChange(clampStrokeWidth(parseFloat(e.currentTarget.value)))
          }
        />
        <span className="thud__draw-slider-val" data-num>
          {sliderValue.toFixed(2)}m
        </span>
      </label>
    </div>
  );
}
